import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Anchor, Ship, Fish } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import fishingBoat from '@/assets/fishing-boat.jpg';
import { useScrollAnimation, scrollAnimationVariants, transitionPresets } from '@/hooks/useScrollAnimation';

const AboutPreview = () => {
  const { ref, isInView } = useScrollAnimation({ margin: '-100px' as const });
  const { t } = useTranslation();

  const features = [
    { icon: Ship, title: t('about.fleet'), desc: t('about.fleetDesc') },
    { icon: Fish, title: t('about.directSales'), desc: t('about.directSalesDesc') },
  ];

  return (
    <section ref={ref} className="section-padding bg-card pattern-overlay relative z-10">
      <div className="container-premium">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image with Reveal Animation */}
          <motion.div
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={scrollAnimationVariants.scaleRotate}
            transition={{ duration: 1, delay: 0.1 }}
            className="relative"
          >
            <motion.div 
              className="relative overflow-hidden rounded-lg shadow-elegant"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <motion.img
                src={fishingBoat}
                alt="Chalutier écossais en mer du Nord"
                className="w-full h-[500px] object-cover"
                initial={{ scale: 1.2 }}
                animate={isInView ? { scale: 1 } : { scale: 1.2 }}
                transition={{ duration: 1.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
            </motion.div>
            
            {/* Floating card with 3D effect */}
            <motion.div
              initial={{ opacity: 0, y: 40, rotateX: 20 }}
              animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              whileHover={{ 
                y: -8, 
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
              className="absolute -bottom-8 -right-8 bg-accent text-accent-foreground p-6 rounded-lg shadow-elegant max-w-xs hidden md:block"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Anchor className="mb-3" size={28} />
              </motion.div>
              <p className="font-serif text-lg font-semibold">{t('about.floatingTitle')}</p>
              <p className="text-sm mt-2 opacity-90">{t('about.floatingDesc')}</p>
            </motion.div>
          </motion.div>

          {/* Content with Staggered Reveal */}
          <motion.div
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={scrollAnimationVariants.staggerContainer}
          >
            <motion.span 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="text-accent font-medium tracking-wider uppercase text-sm block"
            >
              {t('about.label')}
            </motion.span>
            
            <motion.h2 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6"
            >
              {t('about.title')}
            </motion.h2>
            
            <motion.div 
              variants={scrollAnimationVariants.scaleUp}
              transition={{ duration: 0.8 }}
              className="section-divider !mx-0 mb-8"
            />
            
            <motion.p 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="text-muted-foreground leading-relaxed mb-6"
            >
              {t('about.description1')}
            </motion.p>
            
            <motion.p 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="text-muted-foreground leading-relaxed mb-8"
            >
              {t('about.description2')}
            </motion.p>

            {/* Features with hover effects */}
            <motion.div 
              variants={scrollAnimationVariants.staggerContainer}
              className="grid sm:grid-cols-2 gap-6 mb-10"
            >
              {features.map((item, index) => (
                <motion.div
                  key={index}
                  variants={scrollAnimationVariants.staggerItem}
                  transition={transitionPresets.spring}
                  whileHover={{ 
                    x: 8, 
                    transition: { duration: 0.3 } 
                  }}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  <motion.div 
                    className="p-3 bg-accent/10 rounded-lg"
                    whileHover={{ 
                      scale: 1.1, 
                      backgroundColor: 'hsl(var(--accent) / 0.2)' 
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <item.icon className="text-accent" size={24} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to="/societe#nos-metiers" aria-label={t('about.learnMoreAriaLabel')}>
                <Button variant="gold-outline" size="lg">
                  {t('about.learnMore')}
                  <span className="sr-only"> {t('about.learnMoreAriaLabel')}</span>
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
